import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiUrl } from '../core/api-client-config';
import { BulkCompileRun, StartBulkCompileRequest } from '../models';

/**
 * The background half of "compila tutte le risposte".
 *
 * The synchronous `POST .../compile-all-answers` stays where it is, on forms-engine's
 * `CompilingApiService`, and is untouched. These are the routes that queue the same work as a job:
 * nothing here blocks for the several minutes a batch takes, and nothing is lost when the tab that
 * started it is closed.
 */
@Injectable({ providedIn: 'root' })
export class BulkCompileApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = apiUrl('api/app/TenderFormAnswers');

  /** Queues a run and returns immediately. Refused when one is already in flight for this answer. */
  start(
    tenderFormAnswerId: string,
    request: StartBulkCompileRequest = {},
  ): Observable<BulkCompileRun> {
    return this.httpClient.post<BulkCompileRun>(
      `${this.baseUrl}/${tenderFormAnswerId}/compile-all-answers/start`,
      request,
    );
  }

  /**
   * The caller's newest unreviewed run for this answer, suggestions included, or `null` when there is
   * none. Read when the compiling dashboard loads: that is how a run which finished while the
   * applicant was on another page finds its way back to them.
   */
  latest(tenderFormAnswerId: string): Observable<BulkCompileRun | null> {
    return this.httpClient.get<BulkCompileRun | null>(
      `${this.baseUrl}/${tenderFormAnswerId}/compile-all-answers/latest`,
    );
  }

  get(runId: string): Observable<BulkCompileRun> {
    return this.httpClient.get<BulkCompileRun>(
      `${this.baseUrl}/compile-all-answers/runs/${runId}`,
    );
  }

  /**
   * Asks the run to stop. Returns as soon as the request is recorded — the job notices between
   * questions, so the run is usually still `Running` in the response and settles a few seconds later.
   */
  cancel(runId: string): Observable<BulkCompileRun> {
    return this.httpClient.post<BulkCompileRun>(
      `${this.baseUrl}/compile-all-answers/runs/${runId}/cancel`,
      {},
    );
  }

  /**
   * Marks the run reviewed, whether its suggestions were applied or discarded. This is what takes it
   * out of the tracker.
   */
  acknowledge(runId: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.baseUrl}/compile-all-answers/runs/${runId}/acknowledge`,
      {},
    );
  }
}
